import ReconnectingWebSocket from "reconnecting-websocket";
import type { AppEventMap } from "./events/catalog";
import { handleRealtimeRefreshEnvelope, hasRealtimeRefreshTarget } from "./realtime/refresh";
import {
  decodeRealtimeTopicEnvelope,
  isOperatorAsyncOperationEnvelope,
  isRefreshTagInvalidationEnvelope,
  isRefreshTagsUpdatedEnvelope,
  isParticipantBroadcastUpdateEnvelope,
  isParticipantJourneyRefreshEnvelope,
  WS_REALTIME_TOPIC_EVENT,
} from "./realtime/topics";
import type {
  OperatorAsyncOperationEnvelope,
  ParticipantBroadcastUpdateEnvelope,
  ParticipantJourneyRefreshEnvelope,
  RefreshTagInvalidationEnvelope,
  RefreshTagsUpdatedEnvelope,
} from "./realtime/topics";
import { dispatchRefreshTags } from "./refresh_tags_impl";

// ADR-0507 / ADR-0512: use typed event helpers from the catalog (via window global,
// since each bundle is compiled as an IIFE and cannot share module state)
function emitWS<K extends keyof AppEventMap>(name: K, detail: AppEventMap[K]): void {
  if (window.WMUEvents) {
    window.WMUEvents.emit(name, detail);
  } else {
    // Fallback for pages that load ws_client before events.js (shouldn't happen
    // in normal shell-head order, but guard defensively)
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }
}

type FeatureFlagsPayload = {
  data?: {
    flags?: Record<string, boolean>;
  };
};

type SocketLike = {
  addEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
  reconnect?: (code?: number, reason?: string) => void;
  close?: (code?: number, reason?: string) => void;
  readyState?: number;
};

type WMURealtimeMessage = {
  event?: string;
  data?: Record<string, unknown>;
};

declare global {
  interface Window {
    __WMU_WS_BOOTED?: boolean;
  }
}

const WS_OPEN = 1;
const WS_CONNECTING = 0;
const PARTICIPANT_JOURNEY_REFRESH_TAG = "participant.journey";

// ADR-0515: Circuit breaker thresholds
const MAX_RETRIES = 10;
const MAX_BACKOFF_MS = 30_000;

function buildSocketURL(): string {
  const protocol = window.location.protocol === "https:" ? "wss" : "ws";
  return `${protocol}://${window.location.host}/ws`;
}

function wsFactory(url: string): SocketLike {
  return new ReconnectingWebSocket(url, [], {
    maxRetries: Infinity,
    maxReconnectionDelay: MAX_BACKOFF_MS,
    minReconnectionDelay: 1_000,
    reconnectionDelayGrowFactor: 1.6,
    minUptime: 5_000,
    connectionTimeout: 4_000,
  }) as unknown as SocketLike;
}

export class WeMeshUpSocket {
  ws: SocketLike | null;
  // ADR-0515: circuit breaker state
  private retryCount = 0;
  private circuitOpen = false;

  constructor(private readonly createSocket: (url: string) => SocketLike = wsFactory) {
    this.ws = null;
  }

  async init(): Promise<void> {
    const flags = await this.fetchFlags();
    if (!flags.websocket_realtime) return;
    this.connect();
  }

  /** Reset the circuit breaker and reconnect. Called by router on navigation. */
  reset(): void {
    this.retryCount = 0;
    this.circuitOpen = false;
    this.connect();
  }

  async fetchFlags(): Promise<Record<string, boolean>> {
    try {
      const resp = await fetch("/api/features", {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      });
      if (!resp.ok) return {};
      const payload = await resp.json() as FeatureFlagsPayload;
      return payload?.data?.flags || {};
    } catch (_err) {
      return {};
    }
  }

  connect(): void {
    // ADR-0515: don't reconnect while circuit is open
    if (this.circuitOpen) return;

    if (this.ws && (this.ws.readyState === WS_OPEN || this.ws.readyState === WS_CONNECTING)) {
      return;
    }
    const url = buildSocketURL();
    try {
      this.ws = this.createSocket(url);
    } catch (_err) {
      emitWS("wmu:ws-state", { active: false, state: "disconnected" });
      return;
    }

    this.ws.addEventListener("open", () => {
      // ADR-0515: reset counter on successful connection
      this.retryCount = 0;
      this.circuitOpen = false;
      emitWS("wmu:ws-state", { active: true, state: "connected" });
    });

    this.ws.addEventListener("message", (event) => {
      const payload = (event as MessageEvent).data;
      this.handleMessage(payload);
    });

    this.ws.addEventListener("close", () => {
      // ADR-0515: count consecutive failures
      this.retryCount++;
      if (this.retryCount >= MAX_RETRIES) {
        this.tripCircuit();
        return;
      }
      emitWS("wmu:ws-state", { active: false, state: "reconnecting" });
    });

    this.ws.addEventListener("error", () => {
      emitWS("wmu:ws-state", { active: false, state: "disconnected" });
    });

    window.addEventListener("online", () => {
      if (this.circuitOpen) {
        // Re-try once when network comes back online
        this.reset();
        return;
      }
      if (this.ws && typeof this.ws.reconnect === "function") {
        this.ws.reconnect();
      }
    }, { passive: true });
  }

  /**
   * ADR-0515: Trip the circuit breaker after MAX_RETRIES consecutive failures.
   * Shows a persistent toast with a Reconnect button (ADR-0512).
   */
  private tripCircuit(): void {
    this.circuitOpen = true;
    this.ws?.close?.();

    emitWS("wmu:ws-state", { active: false, state: "failed" });

    // ADR-0512: use wmu:flash event (toast.ts listens) instead of calling WMUToast directly
    emitWS("wmu:flash", {
      message: "Live updates unavailable. Click Reconnect to try again.",
      title: "Realtime disconnected",
      variant: "warning",
      persistent: true,
      duration: 0,
      onClick: () => this.reset(),
    });

    window.WMULogger?.warn("ws:circuit-open", new Error("WS circuit breaker tripped"), {
      retries: this.retryCount,
    });
  }

  showShadowAlert(payload: Record<string, unknown>): void {
    const title = String(payload.title || "Your Shadow approaches").trim();
    const body = String(payload.body || "").trim();
    const link = String(payload.link || "/notifications").trim() || "/notifications";

    // ADR-0512: use typed event catalog instead of calling WMUToast directly
    emitWS("wmu:flash", {
      message: body || title,
      title,
      variant: "warning",
      persistent: true,
      duration: 0,
      haptic: "warning",
      onClick: () => {
        window.location.assign(link);
      },
    });

    if (navigator.vibrate && typeof navigator.vibrate === "function") {
      navigator.vibrate([120, 80, 120]);
    }
  }

  private handleParticipantBroadcastUpdate(envelope: ParticipantBroadcastUpdateEnvelope): void {
    emitWS("wmu:broadcast-update", envelope);
    emitWS("wmu:ws-message", { event: "wm-broadcast", payload: envelope.payload });
  }

  private handleAsyncOperationUpdate(envelope: OperatorAsyncOperationEnvelope): void {
    emitWS("wmu:async-operation-update", envelope);
    for (const refreshTarget of envelope.payload.refresh || []) {
      const triggerName = String(refreshTarget || "").trim();
      if (triggerName) {
        this.refreshTargets(triggerName);
      }
    }
  }

  private refreshTargets(...tags: string[]): void {
    dispatchRefreshTags({
      tags,
      source: "ws",
    });
  }

  private handleParticipantJourneyRefresh(envelope: ParticipantJourneyRefreshEnvelope): void {
    emitWS("wmu:participant-journey-refresh", envelope);
    const participantID = String(envelope.payload?.participant_id || "").trim();
    if (participantID && hasRealtimeRefreshTarget(`user:${participantID}`, PARTICIPANT_JOURNEY_REFRESH_TAG)) {
      return;
    }
    dispatchRefreshTags({
      tags: [envelope.topic],
      reason: envelope.payload.reason,
      source: "ws",
      topic: envelope.topic,
    });
  }

  private handleRefreshTagInvalidation(envelope: RefreshTagInvalidationEnvelope): void {
    dispatchRefreshTags({
      tags: envelope.payload.tags,
      reason: envelope.payload.reason,
      source: envelope.payload.source || "ws",
      topic: envelope.topic,
    });
  }

  private handleRefreshTagsUpdated(envelope: RefreshTagsUpdatedEnvelope): void {
    emitWS("wmu:ws-message", { event: "wm-realtime-refresh", payload: envelope.payload });
    handleRealtimeRefreshEnvelope(envelope);
  }

  private handleRealtimeTopic(raw: unknown): boolean {
    const envelope = decodeRealtimeTopicEnvelope(raw);
    if (!envelope) return false;
    if (isParticipantBroadcastUpdateEnvelope(envelope)) {
      this.handleParticipantBroadcastUpdate(envelope);
      return true;
    }
    if (isOperatorAsyncOperationEnvelope(envelope)) {
      this.handleAsyncOperationUpdate(envelope);
      return true;
    }
    if (isParticipantJourneyRefreshEnvelope(envelope)) {
      this.handleParticipantJourneyRefresh(envelope);
      return true;
    }
    if (isRefreshTagsUpdatedEnvelope(envelope)) {
      this.handleRefreshTagsUpdated(envelope);
      return true;
    }
    if (isRefreshTagInvalidationEnvelope(envelope)) {
      this.handleRefreshTagInvalidation(envelope);
      return true;
    }
    return false;
  }

  handleMessage(raw: unknown): void {
    let message: WMURealtimeMessage | null = null;
    try {
      message = JSON.parse(String(raw || "")) as WMURealtimeMessage;
    } catch (_err) {
      return;
    }
    if (!message || typeof message !== "object") return;

    const evt = String(message.event || "").trim();
    const payload = message.data && typeof message.data === "object" ? message.data : {};
    if (evt === WS_REALTIME_TOPIC_EVENT && this.handleRealtimeTopic(message.data)) {
      return;
    }
    if (evt) {
      emitWS("wmu:ws-message", { event: evt, payload });
    }
    if (evt === "notification" || evt === "notification_broadcast" || evt === "faction_notification") {
      const badge = document.getElementById("notif-badge");
      if (badge && window.htmx) {
        window.htmx.ajax("GET", "/api/notifications/unread-count", { target: "#notif-badge", swap: "outerHTML" });
      }
    }

    const type = String(payload.type || "").trim();
    if ((evt === "notification" || evt === "notification_broadcast") && type.indexOf("shadow_reveal") === 0) {
      emitWS("wmu:shadow-reveal", payload);
      if (type === "shadow_reveal_alert") {
        this.showShadowAlert(payload);
      }
    }

    if (evt === "score_updated") {
      dispatchRefreshTags({ tags: ["score_updated"], source: "ws", topic: "score_updated" });
    }
  }
}

// Expose reset() so the router can call window.WMUWsClient.reset() on navigation
declare global {
  interface Window {
    WMUWsClient?: WeMeshUpSocket;
  }
}

function boot(): void {
  if (window.__WMU_WS_BOOTED) return;
  window.__WMU_WS_BOOTED = true;
  const client = new WeMeshUpSocket();
  window.WMUWsClient = client;
  void client.init();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
